import { useOverview, useProfile } from "@calorio/core";
import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";

import { useCalorio } from "./providers";
import { NAV, PUBLIC_ROUTES, ROUTES } from "./routes";
import { useSectionPager } from "./section-pager";
import { markSetupDone, setupDone as storedSetupDone } from "./setup-done";
import { FirstRun } from "../pages/FirstRun";
import { SignIn } from "../pages/SignIn";
import { useSession } from "./session";

/**
 * The routed screen, optionally at a location other than the current one.
 *
 * One table, rendered twice. The peek pane passes `at`, so the neighbour is
 * built by exactly the same routes as the screen you are on — including its
 * params — rather than by a second, drifting copy of the mapping.
 */
function Screen({ at }: { at?: string }) {
  return (
    <Routes location={at}>
      {ROUTES.map((route) => (
        <Route key={route.path} path={route.path} element={route.element} />
      ))}
      <Route path="*" element={<p className="muted">Nothing here.</p>} />
    </Routes>
  );
}

/**
 * Which address is being asked for, before anything is asked of the API.
 *
 * The whole of `App`, so that the hooks the shell needs live one component
 * down and do not run for a visitor who will never reach it. A shared meal is
 * the one address that answers a stranger: it is decided ahead of the session
 * gate *and* ahead of the loading state, because the page does not need to know
 * who you are to render the meal, and making somebody wait on a probe that will
 * come back "nobody" is a blank screen for no reason. The footer is the only
 * part that cares, and by then `useSession` has an answer.
 */
export function App() {
  const location = useLocation();

  const shared = PUBLIC_ROUTES.some((route) =>
    new RegExp(`^${route.path.replace(/:[^/]+/g, "[^/]+")}$`).test(location.pathname),
  );

  if (shared) {
    return (
      <Routes>
        {PUBLIC_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </Routes>
    );
  }

  return <Shell />;
}

/**
 * Who is signed in — and nothing that assumes an answer.
 *
 * Split out of `App` for one reason, and it is about somebody else's data plan
 * rather than about tidiness: a component's hooks all run before its first
 * `return`, so a day's overview and the user's profile were being fetched for
 * every stranger opening a shared link — two authenticated requests that could
 * only ever be answered 401, on the one page in this app meant to be fast for
 * a person with no account. Moving the shell down a level is what makes the
 * early return in `App` actually early.
 *
 * `SignedIn` is split out of this for the same reason and against the same
 * mistake: those two queries lived here, above the gate below, so a visitor
 * with no session fired them and collected two 401s before the app had decided
 * whether anybody was signed in. No session and a dead session are the same
 * 401, so what the sign-in screen said to a first-time visitor was "your
 * session expired. Sign in again to pick up where you left off — everything you
 * logged is still here". This component now holds only the question, and
 * nothing is asked of the API until it has an answer.
 */
function Shell() {
  const { status } = useSession();

  // Nothing here renders without a session — every screen's first
  // act is to ask the API for something. Showing the shell and letting each
  // page fail is how a signed-out visitor ended up reading "your session has
  // expired" with no way to do anything about it.
  if (status === "loading") return <p className="muted" style={{ padding: "var(--sp-5)" }}>…</p>;
  if (status === "signed-out") return <SignIn />;

  return <SignedIn />;
}

/** What somebody with a session gets: setup, or the app. */
function SignedIn() {
  const { signOut } = useSession();
  const ctx = useCalorio();
  const profile = useProfile(ctx);
  const overview = useOverview(ctx, 30);
  /**
   * Skipping is a real exit; it must not re-appear on the next render, and it
   * must not re-appear on the next load either — so the initial value comes
   * from storage rather than being `false` every mount. See `setup-done.ts`.
   */
  const [setupDone, setSetupDone] = useState(storedSetupDone);
  // The page cannot scroll sideways, so a sideways drag pages between the five
  // destinations instead. The neighbour is rendered for real and travels under
  // the thumb — see `section-pager.ts` for why it is mounted rather than
  // animated after the fact.
  const pager = useSectionPager();
  const { pathname } = useLocation();

  /**
   * Nobody has ever logged anything and there is no budget to log against.
   *
   * Both conditions, not either. A budget with no entries is somebody who set up
   * and has not eaten yet — Today's own empty state handles that, and throwing
   * them back into setup would be a loop. Entries with no budget is somebody who
   * skipped, and dragging them back would make "skip" a lie.
   *
   * Held until both queries answer, because rendering setup at a signed-in user
   * with a year of history for one frame is worse than a moment of nothing.
   *
   * `setupDone` is what the server cannot say. Somebody who skipped and has not
   * eaten yet still answers both conditions above on every later load, so the
   * stored answer is the only thing standing between them and the form they
   * already dismissed.
   */
  const bare =
    !setupDone &&
    overview.data !== undefined &&
    profile.data !== undefined &&
    overview.data.intake.logged_days === 0 &&
    overview.data.intake.target_kcal === null;

  /* The bar shows five of seven, so the tab you are on can be off its edge —
     arriving on Weight by swipe or by link left a bar with nothing highlighted.
     Bring the current link into view when the address changes, and only then:
     scrolling it on every render would fight a thumb that is browsing the bar.
     Keyed on `bare` as well, because setup renders no bar at all: leaving it
     puts one on screen at an address that has not changed, and an effect keyed
     on the address alone would have fired into a document with no bar in it
     and never again. */
  useEffect(() => {
    document
      .querySelector('.nav__links [aria-current="page"]')
      ?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  }, [pathname, bare]);

  if (bare) {
    return (
      <main className="app__main">
        {/* Both of `FirstRun`'s exits land here, so a finished setup records the
            answer too. Harmless: a finished setup has a budget, so the gate
            above could not fire for it either way. */}
        <FirstRun
          profile={profile.data}
          onDone={() => {
            markSetupDone();
            setSetupDone(true);
          }}
        />
      </main>
    );
  }

  return (
    <div className="app">
      <nav className="nav" aria-label="Main">
        <NavLink to="/" className="nav__brand">
          Calorio
        </NavLink>

        {/* One list, one label per destination, both platforms. `end` is only
            on "/" — without it the root link matches every route. */}
        <div className="nav__links">
          {NAV.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.end} className="nav__link">
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="nav__actions">
          <button className="nav__signout" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </nav>

      <main className="app__main">
        {/* Two panes on one track. The second exists only during a drag, and
            holds the destination you are pulling towards — rendered by the same
            route table at a location it has not navigated to yet, which is what
            `<Routes location>` is for. */}
        <div className="pager" ref={pager.track}>
          <div className="pager__pane">
            <Screen />
          </div>

          {pager.peek && (
            <div
              className="pager__peek"
              data-side={pager.peek.direction > 0 ? "next" : "prev"}
              style={{ top: pager.peek.top }}
              // Hidden from assistive technology and from the tab order both:
              // it is a duplicate of a whole screen, and a second copy of every
              // control on it is not something to land on by pressing Tab.
              aria-hidden="true"
              inert
            >
              <Screen at={pager.peek.to} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
